import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { usePrefs, useT } from "@/context/PreferencesContext";
import { MagnifyingGlass, ShoppingBag, User, SignOut, Globe } from "@phosphor-icons/react";
import { useState } from "react";

export function Navbar() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { language, setLanguage, currency, setCurrency } = usePrefs();
  const t = useT();
  const navigate = useNavigate();
  const [q, setQ] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/catalogue?q=${encodeURIComponent(q.trim())}`);
  };

  const toggleLang = () => setLanguage(language === "fr" ? "en" : "fr");
  const toggleCur = () => setCurrency(currency === "cad" ? "usd" : "cad");

  return (
    <header className="sticky top-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 h-16 flex items-center gap-6">
        <Link to="/" data-testid="nav-home" className="font-display text-2xl uppercase tracking-tighter">
          MERCH<span className="text-[#FF3B00]">/</span>HUB
        </Link>
        <nav className="hidden md:flex items-center gap-6 text-sm uppercase tracking-widest">
          <Link to="/catalogue" data-testid="nav-catalog" className="hover:text-[#FF3B00] transition-colors">{t("nav.catalog")}</Link>
          <Link to="/pilotes" data-testid="nav-artists" className="hover:text-[#FF3B00] transition-colors">{t("nav.artists")}</Link>
          <Link to="/catalogue?category=hoodie" className="hover:text-[#FF3B00] transition-colors">{t("nav.hoodies")}</Link>
          <Link to="/catalogue?category=tshirt" className="hover:text-[#FF3B00] transition-colors">{t("nav.tshirts")}</Link>
          <Link to="/catalogue?category=limited" className="hover:text-[#FF3B00] transition-colors">{t("nav.limited")}</Link>
        </nav>
        <form onSubmit={submit} className="flex-1 max-w-md hidden md:flex items-center gap-2 border border-white/20 px-3 py-2 focus-within:border-[#FF3B00]">
          <MagnifyingGlass size={16} />
          <input
            data-testid="nav-search"
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={t("nav.search_placeholder")}
            className="bg-transparent outline-none text-sm w-full placeholder:text-white/40"
          />
        </form>
        <div className="ml-auto flex items-center gap-3">
          <button
            onClick={toggleLang}
            data-testid="nav-lang-toggle"
            title={language === "fr" ? "Switch to English" : "Passer en français"}
            className="text-xs uppercase tracking-widest hover:text-[#FF3B00] flex items-center gap-1">
            <Globe size={14} /> {language === "fr" ? "FR" : "EN"}
          </button>
          <button
            onClick={toggleCur}
            data-testid="nav-cur-toggle"
            title={currency === "cad" ? "Switch to USD" : "Passer en CAD"}
            className="text-xs uppercase tracking-widest hover:text-[#FF3B00]">
            {currency === "cad" ? "🇨🇦 CAD" : "🇺🇸 USD"}
          </button>
          <Link to="/panier" data-testid="nav-cart" className="relative hover:text-[#FF3B00] transition-colors">
            <ShoppingBag size={22} />
            {count > 0 && (
              <span className="absolute -top-2 -right-2 bg-[#FF3B00] text-white text-[10px] font-bold px-1.5 py-0.5 min-w-[18px] text-center">
                {count}
              </span>
            )}
          </Link>
          {user && user.id ? (
            <div className="flex items-center gap-3 text-sm">
              {user.role === "artist" && (
                <Link to="/pilote/dashboard" data-testid="nav-artist-dash" className="uppercase tracking-widest hover:text-[#FF3B00]">{t("nav.studio")}</Link>
              )}
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin" className="uppercase tracking-widest hover:text-[#FF3B00]">{t("nav.admin")}</Link>
              )}
              {user.role === "fan" && (
                <Link to="/commandes" data-testid="nav-orders" className="uppercase tracking-widest hover:text-[#FF3B00]">{t("nav.orders")}</Link>
              )}
              <button onClick={logout} data-testid="nav-logout" className="hover:text-[#FF3B00]" title="Se déconnecter">
                <SignOut size={20} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm">
              <Link to="/login" data-testid="nav-login" className="px-3 py-1.5 hover:text-[#FF3B00] uppercase tracking-widest">{t("nav.login")}</Link>
              <Link to="/register" data-testid="nav-register" className="px-4 py-2 bg-[#FF3B00] text-white uppercase tracking-widest hover:bg-[#E03500]">{t("nav.register")}</Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-white/10 mt-32 py-16 px-6 lg:px-10 max-w-[1600px] mx-auto">
      <div className="grid md:grid-cols-4 gap-8">
        <div>
          <div className="font-display text-3xl uppercase">MERCH/HUB</div>
          <p className="mt-3 text-sm text-white/60">
            La marketplace pour la merch des pilotes indépendants. Commandez, expédiez, vibrez.
          </p>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-white/40 mb-3">Boutique</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/catalogue">Catalogue</Link></li>
            <li><Link to="/pilotes">Pilotes</Link></li>
            <li><Link to="/catalogue?category=limited">Éditions Limitées</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-white/40 mb-3">Vendeurs</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/register">Devenir vendeur</Link></li>
            <li><Link to="/pilote/dashboard">Studio</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-white/40 mb-3">Infos</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/faq" data-testid="footer-faq" className="hover:text-[#FF3B00]">FAQ &amp; Politiques</Link></li>
            <li><Link to="/faq#remboursement" className="hover:text-[#FF3B00]">Remboursement</Link></li>
            <li><Link to="/faq#livraison" className="hover:text-[#FF3B00]">Livraison</Link></li>
            <li><a href="mailto:merchhub8@gmail.com" className="hover:text-[#FF3B00]">merchhub8@gmail.com</a></li>
          </ul>
        </div>
      </div>
      <div className="mt-12 pt-6 border-t border-white/10 text-xs text-white/40 uppercase tracking-widest flex flex-wrap gap-3 justify-between">
        <span>© 2026 Merch Hub — <Link to="/faq" className="hover:text-[#FF3B00]">Politiques</Link></span>
        <span>Made with rage</span>
      </div>
    </footer>
  );
}

export function Marquee({ text = "NOUVELLE COLLECTION" }) {
  const items = Array.from({ length: 12 }, (_, i) => text);
  return (
    <div className="overflow-hidden border-y border-white/10 py-4 bg-black">
      <div className="flex animate-marquee whitespace-nowrap">
        {[...items, ...items].map((t, i) => (
          <span key={i} className="font-display text-6xl md:text-8xl uppercase mx-8 text-white/10">
            {t} <span className="text-[#FF3B00]">■</span>
          </span>
        ))}
      </div>
    </div>
  );
}
